import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { colorsSemantic } from "../../theme/tokens/colors-semantic.js";

// Fixed categorical order (hues assigned in sequence, never cycled). The last
// slot is the "Sonstige" catch-all. Lives in the theme so the dashboard's other
// cards can stay in step.
const COLORS = colorsSemantic.attention.categorical;

/**
 * SparteDonut — claim distribution by Sparte. SVG rather than a CSS
 * conic-gradient so each segment can carry its own <title> for accessibility and
 * hover, and so the geometry is testable.
 *
 * `size` scales the whole thing: 140 for a full-width card, 96 for the
 * dashboard's narrow right-hand column.
 */
export default function SparteDonut({ total = 0, segments = [], size = 140 }) {
  const stroke = size * (20 / 140);
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  const compact = size < 120;

  let offset = 0;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: compact ? "15px" : 3, flexWrap: "wrap" }}>
      <Box sx={{ position: "relative", width: size, height: size, flex: "none" }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <g transform={`rotate(-90 ${center} ${center})`}>
            {/* Track — also the empty state. */}
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="#eef1f5"
              strokeWidth={stroke}
            />
            {total > 0 &&
              segments.map((segment, i) => {
                const fraction = segment.count / total;
                const length = fraction * circumference;
                const gap = segments.length > 1 ? 2 : 0;
                const dash = Math.max(length - gap, 0);
                const element = (
                  <circle
                    key={i}
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke={COLORS[i % COLORS.length]}
                    strokeWidth={stroke}
                    strokeDasharray={`${dash} ${circumference - dash}`}
                    strokeDashoffset={-offset}
                  >
                    <title>{`${segment.label}: ${segment.count} (${segment.pct} %)`}</title>
                  </circle>
                );
                offset += length;
                return element;
              })}
          </g>
        </svg>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: compact ? "16px" : "22px",
              fontWeight: 700,
              color: colorsSemantic.text.strong,
              lineHeight: 1,
            }}
          >
            {total}
          </Typography>
          <Typography sx={{ fontSize: compact ? "9px" : "11px", color: colorsSemantic.text.muted }}>
            Schäden
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: compact ? 120 : 140,
          display: "flex",
          flexDirection: "column",
          gap: compact ? "7px" : "9px",
        }}
      >
        {segments.length === 0 ? (
          <Typography sx={{ fontSize: "13px", color: colorsSemantic.text.muted }}>
            Noch keine Schäden erfasst.
          </Typography>
        ) : (
          segments.map((segment, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: compact ? "8px" : "10px" }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "3px",
                  backgroundColor: COLORS[i % COLORS.length],
                  flex: "none",
                }}
              />
              <Typography
                title={segment.label}
                sx={{ fontSize: "12px", color: colorsSemantic.text.mutedStrong, flex: 1, minWidth: 0 }}
                noWrap
              >
                {segment.label}
              </Typography>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, flex: "none" }}>
                {segment.pct} %
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}

SparteDonut.propTypes = {
  total: PropTypes.number,
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      count: PropTypes.number,
      pct: PropTypes.number,
    })
  ),
  size: PropTypes.number,
};
